import { useMemo } from "react";
import { FormContainer, MandatoryNote } from "./common";
import { MenuItem, TextField } from "@mui/material";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface Props {
  countries: string[];
  currencies: string[];
  sectors: Record<string, string>;
}

export const Step1 = (props: Props) => {
  const { t } = useTranslation();

  const { countryOptions, currencyOptions, sectorOptions } = useMemo(() => {
    const currencies = props.currencies.slice().sort();
    return {
      countryOptions: props.countries.map((code) => (
        <MenuItem key={code} value={code}>
          {t(`country.${code}translation`)}
        </MenuItem>
      )),
      currencyOptions: currencies.map((code) => (
        <MenuItem key={code} value={code}>
          {code}
        </MenuItem>
      )),
      sectorOptions: Object.entries(props.sectors).map(([code, labelKey]) => (
        <MenuItem key={code} value={code}>
          {t(labelKey)}
        </MenuItem>
      )),
    };
  }, [props, t]);

  return (
    <FormContainer>
      <Controller
        name={"companyName"}
        render={({ field, fieldState: { error } }) => (
          <TextField
            required
            variant={"outlined"}
            {...field}
            label={t(`page.selfRegistration.field.${field.name}.label`)}
            error={!!error}
            helperText={error ? t(error.message as string) : undefined}
          />
        )}
      />

      <Controller
        name={"street"}
        render={({ field, fieldState: { error } }) => (
          <TextField
            required
            variant={"outlined"}
            {...field}
            label={t(`page.selfRegistration.field.${field.name}.label`)}
            error={!!error}
            helperText={error ? t(error.message as string) : undefined}
          />
        )}
      />

      <Controller
        name={"zipCode"}
        render={({ field, fieldState: { error } }) => (
          <TextField
            required
            variant={"outlined"}
            {...field}
            label={t(`page.selfRegistration.field.${field.name}.label`)}
            error={!!error}
            helperText={error ? t(error.message as string) : undefined}
          />
        )}
      />

      <Controller
        name={"city"}
        render={({ field, fieldState: { error } }) => (
          <TextField
            required
            variant={"outlined"}
            {...field}
            label={t(`page.selfRegistration.field.${field.name}.label`)}
            error={!!error}
            helperText={error ? t(error.message as string) : undefined}
          />
        )}
      />

      <Controller
        name={"country"}
        render={({ field, fieldState: { error } }) => (
          <TextField
            required
            select
            variant={"outlined"}
            {...field}
            label={t(`page.selfRegistration.field.${field.name}.label`)}
            error={!!error}
            helperText={error ? t(error.message as string) : undefined}
          >
            {countryOptions}
          </TextField>
        )}
      />

      <Controller
        name={"sector"}
        render={({ field, fieldState: { error } }) => (
          <TextField
            required
            select
            variant={"outlined"}
            {...field}
            label={t(`page.selfRegistration.field.${field.name}.label`)}
            error={!!error}
            helperText={error ? t(error.message as string) : undefined}
          >
            {sectorOptions}
          </TextField>
        )}
      />

      <Controller
        name={"volume"}
        render={({ field, fieldState: { error } }) => (
          <TextField
            required
            variant={"outlined"}
            type={"number"}
            {...field}
            label={t(`page.selfRegistration.field.${field.name}.label`)}
            error={!!error}
            helperText={error ? t(error.message as string) : t(`page.selfRegistration.field.${field.name}.helper`)}
          />
        )}
      />

      <Controller
        name={"currency"}
        render={({ field, fieldState: { error } }) => (
          <TextField
            required
            select
            variant={"outlined"}
            {...field}
            label={t(`page.selfRegistration.field.${field.name}.label`)}
            error={!!error}
            helperText={error ? t(error.message as string) : undefined}
          >
            {currencyOptions}
          </TextField>
        )}
      />

      <Controller
        name={"suppliers"}
        render={({ field, fieldState: { error } }) => (
          <TextField
            required
            variant={"outlined"}
            {...field}
            label={t(`page.selfRegistration.field.${field.name}.label`)}
            multiline
            minRows={5}
            inputProps={{ maxLength: 250 }}
            error={!!error}
            helperText={error ? t(error.message as string) : t(`page.selfRegistration.field.${field.name}.helper`)}
          />
        )}
      />

      <Controller
        name={"reasons"}
        render={({ field, fieldState: { error } }) => (
          <TextField
            required
            variant={"outlined"}
            {...field}
            label={t(`page.selfRegistration.field.${field.name}.label`)}
            multiline
            minRows={5}
            inputProps={{ maxLength: 250 }}
            error={!!error}
            helperText={error ? t(error.message as string) : t(`page.selfRegistration.field.${field.name}.helper`)}
          />
        )}
      />

      <Controller
        name={"taxId"}
        render={({ field, fieldState: { error } }) => (
          <TextField
            variant={"outlined"}
            {...field}
            label={t(`page.selfRegistration.field.${field.name}.label`)}
            error={!!error}
            helperText={error ? t(error.message as string) : undefined}
          />
        )}
      />

      <MandatoryNote />
    </FormContainer>
  );
};
